import React, { forwardRef } from 'react';
import styled from 'styled-components';

import { ThemeConfig } from '#/service/config';
import {
  ButtonShape,
  ButtonVariant,
  ButtonVariantConfig,
} from '#/service/constants';
import { Dictionary } from '#/service/utils';

import { focusShadow } from '../common';
import { Spinner, SpinnerThickness } from '../Spinner';

interface StyledProps {
  theme: ThemeConfig;
  $variant: ButtonVariant;
}

export interface BaseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $variant: ButtonVariant;
}

const buttonShapeRadius: Dictionary<ButtonShape, string> = {
  [ButtonShape.Pill]: '100rem',
  [ButtonShape.Squared]: '0',
  [ButtonShape.Rounded]: '0.44rem',
};

export const getBorderRadius = ({ theme, $variant }: StyledProps) => {
  const variantConfig = $variant?.toLowerCase() as ButtonVariantConfig;
  const shape = theme.button?.[variantConfig]?.shape ?? ButtonShape.Pill;

  if (!Object.values(ButtonVariant).includes($variant)) {
    console.warn('Invalid button variant:', $variant);
  }

  return `border-radius: ${buttonShapeRadius[shape]};`;
};

export const getVariantStyle = (props: StyledProps) => {
  const { $variant } = props;

  switch ($variant) {
    case ButtonVariant.Ghost:
      return `color: var(--buttonPrimaryForeground);
      background-color: transparent;
      border: none;`;
    case ButtonVariant.Primary:
      return `color: var(--buttonPrimaryForeground);
      background-color: var(--buttonPrimaryBackground);
      border: solid var(--buttonPrimaryBorder);`;
    case ButtonVariant.Secondary:
      return `color: var(--buttonSecondaryForeground);
      background-color: var(--buttonSecondaryBackground);
      border: solid var(--buttonSecondaryBorder);`;
    case ButtonVariant.Promo:
      return `color: var(--buttonPromoForeground);
      background-color: var(--buttonPromoBackground);
      border: solid var(--buttonPromoBorder);`;

    default:
      break;
  }
};

export const BaseButton = styled.button<BaseButtonProps>`
  display: inline-flex;
  align-items: center;
  outline: none;
  font-size: inherit;
  line-height: 1rem;
  word-break: break-word;
  padding: 0.5rem 2rem;

  &:hover:enabled {
    opacity: 0.7;
  }

  &.focus-visible {
    ${focusShadow}
  }

  ${getVariantStyle}
  ${getBorderRadius}

  ${({ disabled }) => (disabled ? 'opacity: 0.3' : 'cursor: pointer')};
`;

BaseButton.displayName = 'BaseButton';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual type of the button */
  variant?: ButtonVariant;

  /** Adds a spinner on top of the button to signal awaiting response */
  isLoading?: boolean;

  /** Custom text that will appear alongside the loading spinner. By default, only spinner will be shown */
  loadingText?: string;
}

/** Basic button component */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = ButtonVariant.Primary,
      isLoading = false,
      children = null,
      loadingText = '',
      type = 'button',
      disabled,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled ?? isLoading;

    return (
      <BaseButton
        ref={ref}
        type={type}
        {...rest}
        $variant={variant}
        disabled={isDisabled}
      >
        {isLoading ? (
          <>
            <StyledSpinner
              animationDuration="0.7s"
              thickness={SpinnerThickness.Bold}
            />
            {loadingText}
          </>
        ) : (
          children
        )}
      </BaseButton>
    );
  }
);

Button.displayName = 'Button';

const StyledSpinner = styled(Spinner)`
  margin-right: 0.2rem;
`;

export default Button;
